import { useState, useEffect, useMemo } from 'react';
import {
    Pause,
    CheckCircle,
    XCircle,
    Clock,
    Plus,
    Loader2,
    ChevronRight,
    Trash2,
    RefreshCw,
    Image as ImageIcon,
    Store,
    AlertCircle,
    CheckCircle2,
    Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    subscribeToUserJobs,
    cancelAutomationJob,
    deleteAutomationJob,
    formatEstimatedTime,
    calculateRemainingTime,
    AutomationJob
} from '../services/automationService';
import { subscribeToJobImages } from '../services/generatedImagesService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Button } from '../components/ui/Button';
import './AutomationsPage.css';

// Upload stats for a single job
interface JobUploadStats {
    total: number;
    uploaded: number;
}

export default function AutomationsPage() {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<AutomationJob[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [uploadStats, setUploadStats] = useState<Record<string, JobUploadStats>>({});
    const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    // Calculate batch info for each job (grouped by promptTitle)
    const batchInfo = useMemo(() => {
        const info: Record<string, { batchNumber: number; totalBatches: number; label: string; cssClass: string }> = {};
        const byPromptTitle: Record<string, AutomationJob[]> = {};

        // Group jobs by promptTitle
        jobs.forEach(job => {
            const key = job.promptTitle;
            if (!byPromptTitle[key]) {
                byPromptTitle[key] = [];
            }
            byPromptTitle[key].push(job);
        });

        // Sort each group by createdAt (oldest first) and assign batch numbers + labels
        Object.values(byPromptTitle).forEach(group => {
            const sorted = [...group].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
            sorted.forEach((job, index) => {
                const batchNumber = index + 1;
                const totalBatches = sorted.length;

                // Determine label based on position in sequence
                let label: string;
                let cssClass: string;
                if (totalBatches === 1) {
                    // Single job - show as terminé
                    label = 'terminé';
                    cssClass = 'done';
                } else if (index === 0) {
                    label = 'départ';
                    cssClass = 'start';
                } else if (index === totalBatches - 1) {
                    label = 'terminé';
                    cssClass = 'done';
                } else {
                    label = 'suite';
                    cssClass = 'ongoing';
                }

                info[job.id] = {
                    batchNumber,
                    totalBatches,
                    label,
                    cssClass
                };
            });
        });

        return info;
    }, [jobs]);

    // Subscribe to job images for upload stats
    useEffect(() => {
        if (jobs.length === 0) return;

        const unsubscribes: (() => void)[] = [];

        jobs.forEach(job => {
            const unsub = subscribeToJobImages(job.id, (images) => {
                // Count all generated images (excluding failed without URL)
                const generated = images.filter(img => img.imageUrl);
                const uploaded = images.filter(img => img.uploadedToShopify === true);
                setUploadStats(prev => ({
                    ...prev,
                    [job.id]: {
                        total: generated.length,
                        uploaded: uploaded.length
                    }
                }));
            });
            unsubscribes.push(unsub);
        });

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [jobs]);

    useEffect(() => {
        if (!userProfile?.uid) return;

        setIsLoading(true);
        const unsubscribe = subscribeToUserJobs(userProfile.uid, (updatedJobs) => {
            setJobs(updatedJobs);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile?.uid]);

    const handleCancelJob = async (jobId: string) => {
        if (!confirm('Êtes-vous sûr de vouloir annuler cette automatisation ?')) return;

        try {
            await cancelAutomationJob(jobId);
        } catch (error) {
            console.error('Error cancelling job:', error);
        }
    };

    const handleDeleteJob = async (jobId: string) => {
        try {
            setDeletingJobId(jobId);
            setDeleteConfirmId(null);
            await deleteAutomationJob(jobId);
        } catch (error) {
            console.error('Error deleting job:', error);
        } finally {
            setDeletingJobId(null);
        }
    };

    const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
    const [fixingJobId, setFixingJobId] = useState<string | null>(null);

    const handleRetryJob = async (jobId: string) => {
        try {
            setRetryingJobId(jobId);
            const resumeAutomationJob = httpsCallable(functions, 'resumeAutomationJob');
            const result = await resumeAutomationJob({ jobId });
            const data = result.data as { success: boolean; newJobId?: string };

            if (data.success && data.newJobId) {
                // Navigate to the new job
                navigate(`/automations/${data.newJobId}`);
            }
        } catch (error) {
            console.error('Error retrying job:', error);
            alert('Erreur lors de la relance du job. Veuillez réessayer.');
        } finally {
            setRetryingJobId(null);
        }
    };

    const handleFixStuckJob = async (jobId: string) => {
        try {
            setFixingJobId(jobId);
            const fixStuckJobs = httpsCallable(functions, 'fixStuckJobs');
            const result = await fixStuckJobs({ jobId });
            const data = result.data as { success: boolean; newJobId?: string; message?: string };

            if (data.success && data.newJobId) {
                // Navigate to the continuation job
                navigate(`/automations/${data.newJobId}`);
            } else if (data.success) {
                alert(data.message || 'Job corrigé avec succès !');
            }
        } catch (error) {
            console.error('Error fixing stuck job:', error);
            alert('Erreur lors de la correction du job. Veuillez réessayer.');
        } finally {
            setFixingJobId(null);
        }
    };

    // Check if a job is stuck (processing for more than 5 minutes)
    const isJobStuck = (job: AutomationJob): boolean => {
        if (job.status !== 'processing') return false;
        // Use startedAt if available, otherwise createdAt
        const jobStartTime = job.startedAt || job.createdAt;
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const isStuck = jobStartTime < fiveMinutesAgo;
        console.log('[isJobStuck]', job.id, 'status:', job.status, 'startedAt:', job.startedAt, 'createdAt:', job.createdAt, 'isStuck:', isStuck);
        return isStuck;
    };




    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock size={18} className="status-icon pending" />;
            case 'processing':
                return <Loader2 size={18} className="status-icon processing spin" />;
            case 'completed':
                return <CheckCircle size={18} className="status-icon completed" />;
            case 'paused':
                return <Pause size={18} className="status-icon paused" />;
            case 'cancelled':
                return <XCircle size={18} className="status-icon cancelled" />;
            case 'failed':
                return <XCircle size={18} className="status-icon failed" />;
            case 'continued':
                return <CheckCircle2 size={18} className="status-icon continued" />;
            case 'stuck-recovered':
                return <AlertCircle size={18} className="status-icon recovered" />;
            default:
                return null;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'En attente';
            case 'processing': return 'En cours';
            case 'completed': return 'Terminé';
            case 'paused': return 'En pause';
            case 'cancelled': return 'Annulé';
            case 'failed': return 'Échoué';
            case 'continued': return 'Continué';
            case 'stuck-recovered': return 'Récupéré';
            default: return status;
        }
    };

    const getModeLabel = (mode: string) => {
        return mode === 'auto-upload' ? 'Upload auto' : 'Vérification manuelle';
    };

    return (
        <div className="automations-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>Automatisations</h1>
                    <p>Générez des images pour plusieurs produits en même temps</p>
                </div>
                <Button
                    variant="primary"
                    leftIcon={<Plus size={18} />}
                    onClick={() => navigate('/automations/new')}
                >
                    Nouvelle automatisation
                </Button>
            </div>

            <div className="jobs-container">
                {isLoading ? (
                    <div className="loading-state">
                        <Loader2 size={32} className="spin" />
                        <p>Chargement des automatisations...</p>
                    </div>
                ) : jobs.length === 0 ? (
                    <div className="empty-state glass">
                        <ImageIcon size={64} className="empty-icon" />
                        <h2>Aucune automatisation</h2>
                        <p>Créez votre première automatisation pour générer des images en lot.</p>
                        <Button
                            variant="primary"
                            leftIcon={<Plus size={18} />}
                            onClick={() => navigate('/automations/new')}
                        >
                            Créer une automatisation
                        </Button>
                    </div>
                ) : (
                    <div className="jobs-list">
                        {jobs.map((job) => (
                            <div
                                key={job.id}
                                className={`job-card glass ${job.status}`}
                                onClick={() => {
                                    console.log('[AutomationsPage] Navigating to job:', job.id, 'full job:', job);
                                    navigate(`/automations/${job.id}`);
                                }}
                            >
                                <div className="job-header">
                                    <div className="job-info">
                                        <div className="job-status">
                                            {getStatusIcon(job.status)}
                                            <span className={`status-label ${job.status}`}>
                                                {getStatusLabel(job.status)}
                                            </span>
                                            {batchInfo[job.id] && (
                                                <span className={`batch-badge ${batchInfo[job.id].cssClass}`}>
                                                    Batch {batchInfo[job.id].batchNumber}/{batchInfo[job.id].totalBatches} - {batchInfo[job.id].label}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="job-title">{job.promptTitle}</h3>
                                        <div className="job-meta">
                                            <span className="shop-name">
                                                <Store size={14} />
                                                {job.shopName}
                                            </span>
                                            <span className="mode-badge">{getModeLabel(job.mode)}</span>
                                            {/* Upload status badge */}
                                            {uploadStats[job.id] && uploadStats[job.id].total > 0 && (
                                                <span className={`upload-status ${uploadStats[job.id].uploaded === 0
                                                    ? 'none'
                                                    : uploadStats[job.id].uploaded === uploadStats[job.id].total
                                                        ? 'complete'
                                                        : 'partial'
                                                    }`}>
                                                    <Upload size={12} />
                                                    {uploadStats[job.id].uploaded}/{uploadStats[job.id].total}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight size={20} className="chevron" />
                                </div>

                                <div className="job-progress">
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{
                                                width: `${(job.progress.completed / job.progress.total) * 100}%`
                                            }}
                                        />
                                        {job.progress.failed > 0 && (
                                            <div
                                                className="progress-failed"
                                                style={{
                                                    width: `${(job.progress.failed / job.progress.total) * 100}%`
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="progress-text">
                                        <span>
                                            {job.progress.completed} / {job.progress.total} images
                                            {job.progress.failed > 0 && (
                                                <span className="failed-count"> ({job.progress.failed} échoués)</span>
                                            )}
                                        </span>
                                        {job.status === 'processing' && job.estimatedTimeMs && (
                                            <span className="time-remaining">
                                                ~{formatEstimatedTime(calculateRemainingTime(job))} restant
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="job-footer" onClick={(e) => e.stopPropagation()}>
                                    <span className="job-date">
                                        {job.createdAt.toLocaleDateString('fr-FR', {
                                            day: 'numeric',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </span>
                                    <div className="job-actions">
                                        {job.status === 'processing' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<XCircle size={14} />}
                                                onClick={() => handleCancelJob(job.id)}
                                            >
                                                Annuler
                                            </Button>
                                        )}
                                        {isJobStuck(job) && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                leftIcon={fixingJobId === job.id ? <Loader2 size={14} className="spin" /> : <AlertCircle size={14} />}
                                                onClick={() => handleFixStuckJob(job.id)}
                                                disabled={fixingJobId === job.id}
                                            >
                                                {fixingJobId === job.id ? 'Correction...' : 'Débloquer'}
                                            </Button>
                                        )}

                                        {(job.status === 'paused' || job.status === 'failed') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={retryingJobId === job.id ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                                                onClick={() => handleRetryJob(job.id)}
                                                disabled={retryingJobId === job.id}
                                            >
                                                {retryingJobId === job.id ? 'Relance...' : 'Réessayer'}
                                            </Button>
                                        )}

                                        {(job.status === 'completed' || job.status === 'cancelled' || job.status === 'failed' || job.status === 'paused') && (
                                            deletingJobId === job.id ? (
                                                <div className="delete-loading">
                                                    <Loader2 size={14} className="spin" />
                                                    <span>Suppression...</span>
                                                </div>
                                            ) : deleteConfirmId === job.id ? (
                                                <div className="delete-confirm">
                                                    <span>Supprimer ?</span>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => handleDeleteJob(job.id)}
                                                        className="delete-confirm-btn"
                                                    >
                                                        Oui
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteConfirmId(null)}
                                                    >
                                                        Non
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    leftIcon={<Trash2 size={14} />}
                                                    onClick={() => setDeleteConfirmId(job.id)}
                                                >
                                                    Supprimer
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>


        </div>
    );
}
